import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { useProjectModelError } from "@planzone/dto";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { createSelector } from "./selector";
import { State, Selector } from "./types";

const initialState: State = [];

function handleRequest(
  state: State,
  { sourceProject, zone }: Boundaries["duplicateProject"]["request"],
) {
  const index = state.findIndex(
    (req) =>
      req.sourceProject.uuid === sourceProject.uuid &&
      req.zone.uuid === zone.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          sourceProject,
          zone,
          request: setRequestStateToPending(),
          error: useProjectModelError,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          sourceProject,
          zone,
          request: setRequestStateToPending(),
          error: useProjectModelError,
        },
      ];
}

function handleSuccess(
  state: State,
  { sourceProject, zone }: Boundaries["duplicateProject"]["success"],
): State {
  const index = state.findIndex(
    (req) =>
      req.sourceProject.uuid === sourceProject.uuid &&
      req.zone.uuid === zone.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          sourceProject,
          zone,
          request: setRequestStateToSuccess(),
          error: useProjectModelError,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          sourceProject,
          zone,
          request: setRequestStateToSuccess(),
          error: useProjectModelError,
        },
      ];
}

function handleFailure(
  state: State,
  { sourceProject, zone, error }: Boundaries["duplicateProject"]["failure"],
) {
  const index = state.findIndex(
    (req) =>
      req.sourceProject.uuid === sourceProject.uuid &&
      req.zone.uuid === zone.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          sourceProject,
          zone,
          request: setRequestStateToFailure(),
          error,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          sourceProject,
          zone,
          request: setRequestStateToFailure(),
          error,
        },
      ];
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.duplicateProjectRequested]: handleRequest,
    [events.duplicateProjectSucceeded]: handleSuccess,
    [events.duplicateProjectFailed]: handleFailure,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
